import { API_BASE as BASE } from '../lib/apiBase';
// Thin fetch wrapper that targets the Express API and attaches the admin JWT.
// All admin pages should go through here so auth + base URL stay in one place.

const TOKEN_KEY = 'vp_admin_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

type Init = Omit<RequestInit, 'body'> & { body?: any; auth?: boolean };

async function request<T = any>(path: string, init: Init = {}): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as any) };
  const isFormData = init.body instanceof FormData;
  if (!isFormData && init.body !== undefined) headers['Content-Type'] = 'application/json';
  const token = getToken();
  if (token && init.auth !== false) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    body: isFormData ? init.body : init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  if (res.status === 401) {
    clearToken();
    // Soft redirect — caller will re-route via ProtectedRoute on next render.
    if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/admin/login')) {
      window.location.assign('/admin/login');
    }
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = text;
    try {
      message = JSON.parse(text).error || text;
    } catch {
      /* not JSON */
    }
    throw new Error(message || `Request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as any;
  return res.json();
}

export const api = {
  get: <T = any>(p: string) => request<T>(p),
  post: <T = any>(p: string, body?: any) => request<T>(p, { method: 'POST', body }),
  put:  <T = any>(p: string, body?: any) => request<T>(p, { method: 'PUT',  body }),
  del:  <T = any>(p: string)            => request<T>(p, { method: 'DELETE' }),

  login: (username: string, password: string) =>
    request<{ token: string; user: { username: string; role: string } }>('/api/auth/login', {
      method: 'POST', body: { username, password }, auth: false,
    }),

  uploadFiles: async (files: File[], prefix = 'uploads'): Promise<string[]> => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    const res = await request<{ urls: string[] }>(
      `/api/admin/uploads?prefix=${encodeURIComponent(prefix)}`,
      { method: 'POST', body: fd }
    );
    return res.urls;
  },

  deleteUpload: (url: string) => request('/api/admin/uploads', { method: 'DELETE', body: { url } }),
  patch: <T = any>(p: string, body?: any) => request<T>(p, { method: 'PATCH', body }),

  /**
   * Upload one file and return its public URL. With S3 configured the browser
   * sends the file straight to S3 (signed URL); otherwise it goes through the API.
   */
  uploadFile: async (file: File, prefix = 'uploads'): Promise<string> => {
    const sign = await request<{ mode: 'direct' | 's3'; url?: string; uploadUrl?: string; headers?: Record<string, string> }>(
      `/api/admin/uploads/presign?prefix=${encodeURIComponent(prefix)}`,
      { method: 'POST', body: { filename: file.name, contentType: file.type, size: file.size } }
    );
    if (sign.mode === 's3' && sign.uploadUrl && sign.url) {
      const put = await fetch(sign.uploadUrl, { method: 'PUT', headers: sign.headers, body: file });
      if (!put.ok) throw new Error('Upload to storage failed — check the S3 bucket CORS settings');
      return sign.url;
    }
    const fd = new FormData();
    fd.append('file', file);
    const res = await request<{ urls: string[] }>(`/api/admin/uploads?prefix=${encodeURIComponent(prefix)}`, { method: 'POST', body: fd });
    return res.urls[0];
  },

  /** Download a file that needs the admin token (e.g. the enquiries CSV). */
  download: async (path: string, filename: string) => {
    const res = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${getToken() || ''}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  },
};
